import React, { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilRefresher_UNSTABLE, useRecoilValue } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    IconButton,
    Link,
    ListItemText,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Announcement, MoreVert, OpenInNew } from '@mui/icons-material';
import Currency from 'components/formaters/Currency';
import Date from 'components/formaters/Date';
import useContextMenu from 'hooks/useContextMenu';
import { CarModel, ExportType, PriceTerms, SimpleView } from 'models/StockModel';
import { VoyageModel } from 'models/VoyageModel';
import useInvoices from 'modules/invoices/hooks/useInvoices';
import useInvoiceModals from 'modules/invoices/hooks/useInvoiceModals';
import ProblemMark from 'modules/cars/common/ProblemMark';
import useCarModals from 'modules/cars/hooks/useCarModals';
import useSyncCars from 'modules/cars/hooks/useSyncCars';
import useVoyages from 'modules/voyages/hooks/useVoyages';
import useVoyageModals from 'modules/voyages/hooks/useVoyageModals';
import {
    Dealer,
    Payer,
    PriceFillerInput,
    PriceInput,
    PriceTermEdit,
    PriceWithInvoice,
} from 'modules/voyages/common/FinanceCells';
import { financialVehicleType, formatVehicleTypeForPriceTable } from 'modules/voyages/utils/finance';
import { fillPricesOsaka } from 'modules/voyages/utils/fillPricesOsaka';
import Overstay from 'modules/cars/common/Overstay';
import auth from 'recoil/atoms/auth';
import priceTablesAtom from 'recoil/atoms/priceTables';
import Api from 'utils/api';
import { arrayGrouped, getByPath } from 'utils/common';


type Price = {
    main: number,
    secondary: number,
    freight: number, // meta
    vehicleType: number,
    forklift: number,
    delivery: number,
    service: number,
    discount: number,
    rate: number,
    term?: PriceTerms,
}

export type Prices = {
    [id: number]: Price
}

const FinancesInner = () => {
    const {id} = useParams();
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const navigate = useNavigate();
    const [voyage, setVoyage] = useState<VoyageModel>();
    const {
        getVoyage,
    } = useVoyages();
    const {openPriceTablesModal} = useVoyageModals();
    const {confirmCancelBooking} = useCarModals();
    const {confirmRemoveFromInvoice} = useInvoiceModals();
    const {generateForVoyage} = useInvoices();
    const {enqueueSnackbar} = useSnackbar();
    const [cars, setCars] = useState<CarModel[]>([]);
    const [prices, setPrices] = useState({} as Prices);
    const priceTables = useRecoilValue(priceTablesAtom);
    const refreshPriceTables = useRecoilRefresher_UNSTABLE(priceTablesAtom);

    useSyncCars(setCars);

    const {openMenu: openManage} = useContextMenu<CarModel>(({value, close}) => [
        value.prices?.main.invoice && (
            <MenuItem
                key="main"
                onClick={() => {
                    confirmRemoveFromInvoice(`${value.model} ${value.chassisNumber}`, 'main', value.prices!.main.id).then(load);
                    close();
                }}
            >
                <ListItemText>{t('remove_from_main_invoice')}</ListItemText>
            </MenuItem>
        ),

        value.prices?.secondary.invoice && (
            <MenuItem
                key="secondary"
                onClick={() => {
                    confirmRemoveFromInvoice(`${value.model} ${value.chassisNumber}`, 'secondary', value.prices!.secondary.id).then(load);
                    close();
                }}
            >
                <ListItemText>{t('remove_from_secondary_invoice')}</ListItemText>
            </MenuItem>
        ),

        <MenuItem
            key="cancel"
            onClick={() => {
                confirmCancelBooking([value]).then(() => load());
                close();
            }}
        >
            <ListItemText>{t('cancel_booking')}</ListItemText>
        </MenuItem>,
    ]);

    const load = (full?: boolean) => {
        if (full)
            getVoyage(Number(id))
                .then(res => setVoyage(res.data))
                .catch(() => navigate('/voyages'));

        Api.cars.voyage(id!)
            .then(data => {
                const p: Prices = {};
                data.data.forEach((c: CarModel) => {
                    p[c.id] = {
                        main: c.prices?.main.total || 0,
                        secondary: c.prices?.secondary.total || 0,
                        freight: c.prices?.meta?.freight || 0,
                        vehicleType: c.prices?.meta?.vehicle_type || 0,
                        forklift: c.prices?.meta?.forklift || 0,
                        delivery: c.prices?.meta?.delivery || 0,
                        service: c.prices?.meta?.service || 0,
                        discount: c.prices?.meta?.discount || 0,
                        rate: c.prices?.meta?.rate || 0,
                        term: c.priceTerms,
                    }
                });
                setPrices(p);
                setCars(data.data);
            });
    };

    // eslint-disable-next-line
    useEffect(() => load(true), []);

    const sum = useMemo(() => {
        const temp = {
            cnf: 0,
            fob: 0,
            rate: 0,
        };
        temp.rate = Object.values(prices).find(p => p.rate)?.rate || 0
        Object.values(prices).forEach(p => {
            temp.cnf += p.main;
            temp.fob += p.secondary;
        });
        return temp;
    }, [prices]);

    const [groups, damagedCount] = useMemo(() => {
        return [
            arrayGrouped(cars, 'company.id'),
            cars.filter(car => car.damaged).length,
        ]
    }, [cars]);

    if (!voyage)
        return null;

    const fillPrices = (rate: number) => {
        const table = priceTables.data;
        const update: Prices = {};

        cars.forEach((c: CarModel) => {
            // if already computed price, skip
            if (prices[c.id].secondary || prices[c.id].main) {
                update[c.id] = prices[c.id];
                return;
            }

            const price = {...prices[c.id]};

            // if osaka
            if (voyage.pol.id === 4) {
                const {freight, dis} = fillPricesOsaka(c);
                price.freight = freight;
                price.vehicleType = dis;
            } else {
                const freight = getByPath(table, `port.${voyage.pol.id}.freight.${voyage.pod.id}`, 0);
                const vehicleType = getByPath(table, `port.${voyage.pol.id}.vehicle.${formatVehicleTypeForPriceTable(c)}.${c.exportType}`, 0);

                price.freight = price.freight || freight;
                price.vehicleType = price.vehicleType || vehicleType;
            }

            const forklift = c.damaged ?
                Math.ceil(getByPath(table, `yard.${c.yard!.id}.forklift`, 0) / damagedCount) :
                0;

            price.rate = price.rate || (c.priceTerms === PriceTerms.FOB ? 0 : rate);
            price.forklift = price.forklift || forklift;

            if (c.priceTerms === PriceTerms.FOB) {
                price.discount = getByPath(table, `party.${c.parties?.dealer?.id}.discount`)
                    || getByPath(table, `party.${c.parties?.consignee?.id}.discount`, 0);
                price.main = calcServicePrice(price);
                price.secondary = calcSecondaryPrice(price);
            } else {
                price.discount = getByPath(table, `shipper.${c.company.id}.discount`, 0);
                price.main = calcMainPrice(price);
            }

            update[c.id] = price;
        });

        setPrices(update);
    };

    const setPrice = (value: number, id: number, name: string) => setPrices(old => {
        const price = {
            ...old[id],
            [name]: value,
        };

        if (price.term === PriceTerms.FOB) {
            price.main = calcServicePrice(price);
            price.secondary = calcSecondaryPrice(price);
        } else {
            price.main = calcMainPrice(price);
        }

        return {
            ...old,
            [id]: price,
        }
    });

    const save = () => Api.cars.setFreightPrices(prices)
        .then(() => enqueueSnackbar(t('saved'), {variant: 'success', autoHideDuration: 1000}))
        .then(() => load());

    const generate = () => generateForVoyage(voyage.id)
        .then(() => enqueueSnackbar(t('generated'), {variant: 'success', autoHideDuration: 1000}))
        .then(() => load());

    const generatorEnabled = cars.findIndex(c =>
            c.prices && (
                (c.prices.main && !c.prices.main.invoice)
                || (c.prices.secondary && !c.prices.secondary.invoice)
            )
    ) > -1;

    const sequential = {i: 1};

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            <Card sx={{mb: 2}}>
                <CardHeader
                    title={buildTitle(`${t('voyage')} ${voyage.vessel.name} ${voyage.number}`, voyage.allowDismantled, cars)}
                    action={<>
                        <Button
                            component={Link}
                            size="small"
                            href={`/voyage/${voyage.id}`}
                        >
                            {t('cars')}
                        </Button>
                        {' '}
                        <Button
                            size="small"
                            component="a"
                            href="https://www.bk.mufg.jp/ippan/kinri/list_j/kinri/kawase.html"
                            target="_blank"
                            endIcon={<OpenInNew/>}
                        >MUFG Today</Button>
                        {' '}
                        <Button
                            size="small"
                            component="a"
                            href="http://www.murc-kawasesouba.jp/fx/past_3month.php"
                            target="_blank"
                            endIcon={<OpenInNew/>}
                        >MUFG Stat</Button>
                        {' '}
                        <Button
                            size="small"
                            onClick={() => openPriceTablesModal(voyage?.pol.id).then(refreshPriceTables)}
                        >
                            {t('price_tables')}
                        </Button>
                    </>}
                />
                <CardContent>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} sm={4} display="flex" justifyContent="space-between">
                            <Box>
                                {voyage.pol.name} <br/>
                                <small><Date date={voyage.departedAt || voyage.etd}/></small>
                            </Box>
                            <Box textAlign="center" textTransform="uppercase">
                                {voyage.status}
                            </Box>
                            <Box textAlign="right">
                                {voyage.pod.name} <br/>
                                <small><Date date={voyage.arrivedAt || voyage.eta}/></small>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={2} textAlign="right">
                            $1: <Currency value={sum.rate} /> <br/>
                            C&F: <Currency value={sum.cnf} /> <br/>
                            FOB: <Currency value={sum.fob} currency="usd"/>
                        </Grid>
                        <Grid item xs={6} sm={3}>

                        </Grid>
                        <Grid item xs={6} sm={1}>
                            <small>{t('freight')}:</small>
                            <Box fontSize="1.25rem">
                                <Currency
                                    value={getByPath(priceTables.data, `port.${voyage.pol.id}.freight.${voyage.pod.id}`)}
                                    currency="usd"
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            {my.permissions.has('invoices create') && (
                                <PriceFillerInput
                                    fillPrices={fillPrices}
                                />
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {Object.values(groups).map(group => (
                <CompanyGroup
                    key={group[0].company.id}
                    voyage={voyage}
                    company={group[0].company}
                    group={group}
                    damagedCount={damagedCount}
                    sequential={sequential}
                    openManage={openManage}
                    prices={prices}
                    setPrice={setPrice}
                />
            ))}

            <Box sx={{height: 48}}></Box>

            {cars.length > 0 && my.permissions.has('invoices create') &&
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        position: 'fixed',
                        bottom: theme => theme.spacing(2),
                        right: theme => theme.spacing(5)
                    }}
                >
                    <Button
                        disabled={!generatorEnabled}
                        onClick={generate}
                        variant="contained"
                    >
                        {t('generate_invoices')}
                    </Button>
                    <Button
                        sx={{ml: 1}}
                        onClick={save}
                        variant="contained"
                    >
                        {t('save_prices')}
                    </Button>
                </Box>
            }
        </Container>
    );
};

const calcMainPrice = (price: Price) => Math.round((price.freight + price.vehicleType - price.discount) * price.rate) + price.forklift + price.delivery + price.service;

const calcSecondaryPrice = (price: Price) => price.freight + price.vehicleType - price.discount; // + Math.round((price.forklift + price.delivery + price.service) / (price.rate || 1) * 100) / 100;

const calcServicePrice = (price: Price) => price.forklift + price.delivery + price.service;

const buildTitle = (start: string, allowDismantled: boolean, cars: CarModel[]) => {
    let title = `${start} (${cars.length})`;

    if (allowDismantled)
        title += ` (R.${cars.filter(c => c.exportType !== ExportType.Car).length})`;

    return title;
};


type CompanyGroupProps = {
    voyage: VoyageModel,
    company: SimpleView,
    group: CarModel[],
    damagedCount: number,
    sequential: {i: number},
    openManage: (e: any, v: CarModel) => void,
    prices: Prices,
    setPrice: (value: number, id: number, name: string) => void,
}

const CompanyGroup = ({company, voyage, group, damagedCount, sequential, openManage, prices, setPrice}: CompanyGroupProps) => {
    const {t} = useTranslation('cars');
    const my = useRecoilValue(auth);

    return (
        <Card sx={{mb: 2}}>
            <CardHeader
                title={buildTitle(company.name, voyage.allowDismantled, group)}
            />

            <TableContainer>
                <Table
                    size="small"
                    sx={{
                        '.show-on-hover:not(.force-show)': {
                            visibility: 'hidden',
                        },
                        'tbody tr:hover': {
                            backgroundColor: 'action.hover',
                            '.show-on-hover': {
                                visibility: 'visible',
                            },
                        },
                        '& .MuiTableCell-sizeSmall': {
                            padding: '6px',
                            '&:first-of-type': {
                                paddingLeft: 2,
                            }
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{width: 50}}>#</TableCell>
                            <TableCell sx={{width: 170}}>{t('model')}</TableCell>
                            <TableCell sx={{width: 170}}>{t('type')}</TableCell>
                            <TableCell sx={{width: 80}}>{t('terms')}</TableCell>
                            <TableCell>{t('payer')}</TableCell>
                            <TableCell>{t('dealer')}</TableCell>
                            <TableCell>{t('note')}</TableCell>
                            <TableCell>{t('long')}</TableCell>
                            <TableCell>{t('overstay')}</TableCell>
                            <TableCell sx={{width: 90}}>{t('rate')}</TableCell>
                            <TableCell sx={{width: 90}}>{t('freight')}</TableCell>
                            <TableCell sx={{width: 90}}>+ Razbor</TableCell>
                            <TableCell sx={{width: 90}}>- {t('discount')}</TableCell>
                            {!!damagedCount &&
                                <TableCell sx={{width: 95}}>+ {t('forklift')}</TableCell>
                            }
                            <TableCell sx={{width: 100}}>+ {t('delivery')}</TableCell>
                            <TableCell sx={{width: 95}}>+ {t('service')}</TableCell>
                            <TableCell sx={{width: 140, textAlign: 'right'}}>{t('freight_main')}</TableCell>
                            <TableCell sx={{width: 140, textAlign: 'right'}}>{t('freight_secondary')}</TableCell>
                            <TableCell sx={{width: 42}}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {group.map(car => (
                            <CarRow
                                key={car.id}
                                car={car}
                                i={sequential.i++}
                                canEdit={
                                    my.permissions.has('invoices create')
                                    && !car.prices?.main.invoice
                                    && !car.prices?.secondary.invoice
                                }
                                price={prices[car.id]}
                                setPrice={setPrice}
                                action={e => openManage(e, car)}
                                hasDamaged={!!damagedCount}
                                voyage={voyage}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};


type CarRowProps = {
    car: CarModel,
    i: number,
    canEdit: boolean,
    price: Price,
    setPrice: (value: number, id: number, name: string) => void,
    action: React.MouseEventHandler<HTMLButtonElement>,
    hasDamaged: boolean,
    voyage: VoyageModel,
}

const CarRow = memo(({
                         car,
                         i,
                         canEdit,
                         price,
                         setPrice,
                         action,
                         hasDamaged,
                         voyage,
                     }: CarRowProps) => {

    const changePriceTerms = () => {
        Api.cars.setPriceTerms(car.id, car.priceTerms === PriceTerms.FOB ? PriceTerms.CAF : PriceTerms.FOB)
    };

    return (
        <TableRow>
            <TableCell>
                {i}
            </TableCell>

            <TableCell>
                <Link href={`/car/${car.id}?voyage=${voyage.id}`}>
                    {car.model}
                </Link>
                <br/>
                {car.chassisNumber}
            </TableCell>

            <TableCell>
                {car.vehicleType} {financialVehicleType(car)} <ProblemMark car={car}/>
            </TableCell>

            <TableCell>
                <Box
                    component="span"
                    color={car.priceTerms === PriceTerms.FOB ? 'warning.main' : undefined}
                >
                    {car.priceTerms}
                </Box>
                {' '}
                <PriceTermEdit action={changePriceTerms}/>
            </TableCell>

            <TableCell>
                <Box display="flex">
                    <Payer
                        car={car}
                        main
                    />
                    <Payer
                        car={car}
                        secondary={price.secondary}
                    />
                </Box>
            </TableCell>

            <TableCell>
                <Dealer
                    car={car}
                />
            </TableCell>

            <TableCell>
                {car.note && (
                    <Tooltip
                        title={car.note}
                        placement="top"
                    >
                        <Announcement/>
                    </Tooltip>
                )}
            </TableCell>

            <TableCell>
                {(car.length && car.length > 500) ? `+${car.length - 500}cm` : ''}
            </TableCell>

            <TableCell>
                <Overstay car={car}/>
            </TableCell>

            <TableCell>
                {car.priceTerms !== PriceTerms.FOB && (
                    <PriceInput
                        id={car.id}
                        name="rate"
                        value={price.rate}
                        onChange={setPrice}
                        currency="¥"
                        canEdit={canEdit}
                    />
                )}
            </TableCell>

            <TableCell>
                <PriceInput
                    id={car.id}
                    name="freight"
                    value={price.freight}
                    onChange={setPrice}
                    currency="$"
                    canEdit={canEdit}
                />
            </TableCell>

            <TableCell>
                <PriceInput
                    id={car.id}
                    name="vehicleType"
                    value={price.vehicleType}
                    onChange={setPrice}
                    currency="$"
                    canEdit={canEdit}
                />
            </TableCell>

            <TableCell>
                <PriceInput
                    id={car.id}
                    name="discount"
                    value={price.discount}
                    onChange={setPrice}
                    currency="$"
                    canEdit={canEdit}
                />
            </TableCell>

            {hasDamaged &&
                <TableCell>
                    {!!car.damaged &&
                        <PriceInput
                            id={car.id}
                            name="forklift"
                            value={price.forklift}
                            onChange={setPrice}
                            currency="¥"
                            canEdit={canEdit}
                        />
                    }
                </TableCell>
            }

            <TableCell>
                <PriceInput
                    id={car.id}
                    name="delivery"
                    value={price.delivery}
                    onChange={setPrice}
                    currency="¥"
                    canEdit={canEdit}
                />
            </TableCell>

            <TableCell>
                <PriceInput
                    id={car.id}
                    name="service"
                    value={price.service}
                    onChange={setPrice}
                    currency="¥"
                    canEdit={canEdit}
                />
            </TableCell>

            <TableCell>
                {(!!price.main) &&
                    <PriceWithInvoice
                        price={price.main}
                        currency="jpy"
                        paid={car.prices?.main.paid === car.prices?.main.total}
                        invoice={car.prices?.main.invoice}
                    />
                }
            </TableCell>

            <TableCell>
                {(car.priceTerms !== PriceTerms.FOB && canEdit) ? (
                    <PriceInput
                        id={car.id}
                        name="secondary"
                        value={price.secondary}
                        onChange={setPrice}
                        currency="$"
                        canEdit
                    />
                ) : ((!!price.secondary) &&
                    <PriceWithInvoice
                        price={price.secondary}
                        currency="USD"
                        paid={car.prices?.secondary.paid === car.prices?.secondary.total}
                        invoice={car.prices?.secondary.invoice}
                    />
                )}
            </TableCell>

            <TableCell align="right">
                <IconButton
                    size="small"
                    aria-label="cancellations"
                    onClick={action}
                >
                    <MoreVert fontSize="small"/>
                </IconButton>
            </TableCell>
        </TableRow>
    )
}, (prevProps, nextProps) => (
    prevProps.car === nextProps.car
    && prevProps.price === nextProps.price
));

const Finances = () => {
    return (
        <Suspense fallback={null}>
            <FinancesInner/>
        </Suspense>
    )
};

export default Finances;
